import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React, { useCallback, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { generatedReportsActions } from 'app-components/flux/generated-reports';
import { trackReceiptEvent } from 'app-components/flux/receipts';
import { reportConfigurationsSelectors } from 'app-components/flux/report-configurations';
import { tracker } from 'app-components/utils/core';
import type RootState from 'app-components/utils/redux-store/rootState';

import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

import type { FeaturePage } from 'containers/App/paths';
import { getNavFromKey, makeNavigationItemId, navSections, toNavSectionItem } from 'containers/App/paths';

import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowRightRounded from '@mui/icons-material/KeyboardArrowRightRounded';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import type { FeatureFlagsKeys } from 'app-components/flux/feature-flags/featureFlagsTypes';
import { useHoverStore } from '../HoverContext';
import { useIsNavOpen, useNavExpansion } from '../NavExpansionContext';
import RefreshButton from '../RefreshButton';
import { navBarDiff, navItemInset, navTooltipProps, useNavBarStyle, useNavItemStyles } from '../commonNavStyles';
import { isNavItemVisible } from '../navVisibility';
import { isNavSelected } from '../isNavSelected';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';

const sectionedNavPaths = navSections.flatMap(({ items }) =>
  items.flatMap((item) => getNavFromKey(toNavSectionItem(item).key)?.path ?? []),
);

// Map paragraphs to header tag within navbar
const NAV_MAPPING = { paragraphRegular: 'h4' };

export const GroupNavItem = React.memo(function GroupNavItem() {
  const { classes: navBarClasses } = useNavBarStyle();
  return (
    <div className={navBarClasses.group}>
      <div className={navBarClasses.groupDivider} />
    </div>
  );
});

const useNavItemPopover = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const store = useHoverStore();
  const isNavOpen = useIsNavOpen();
  const { collapsible, closeOverlay } = useNavExpansion();
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<NodeJS.Timeout | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      store.setOverPop(false);
      setAnchorEl(null);
    }, 100);
  }, [cancelClose, store]);

  const handleMouseLeaveAnchor = useCallback(() => {
    if (collapsible || store.getInteractive()) {
      store.setOverPop(true);
      scheduleClose();
    }
  }, [collapsible, scheduleClose, store]);

  const handleEnterAnchor = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      cancelClose();
      setAnchorEl(e.currentTarget);
      store.setOverPop(false);
    },
    [cancelClose, store],
  );

  const handleClickItem = useCallback(() => {
    store.setOverPop(false);
    store.setOverBar(false);
    setAnchorEl(null);
    closeOverlay();
  }, [closeOverlay, store]);

  return {
    anchorEl,
    popoverRef,
    handleEnterAnchor,
    handleMouseLeaveAnchor,
    handleClickItem,
    scheduleClose,
    cancelClose,
    isNavOpen,
  };
};

const PopperNavItem = React.memo(function PopperNavItem({
  label,
  tabName,
  path,
  featureFlag,
  handleClickItem,
  type,
  section,
}: {
  label: string;
  tabName: string;
  path: string;
  featureFlag?: FeatureFlagsKeys;
  handleClickItem: () => void;
  type: 'item' | 'divider';
  section?: string;
}) {
  const show = useAppSelector((state) =>
    featureFlag ? featureFlagsSelectors.getFeatureFlag(state, featureFlag) : true,
  );

  if (!show) {
    return null;
  }

  if (type === 'divider') {
    return <Divider sx={{ width: '100%' }} />;
  }

  const handleClick = () => {
    tracker.track(tracker.events.navMenuClick, { menuItem: label, section });
    handleClickItem();
  };

  return (
    <ButtonBase
      id={`nav-popover-item-${tabName}`}
      data-testid={`nav-popover-item-${tabName}`}
      onClick={handleClick}
      disableRipple
      disableTouchRipple
      component={NavLink}
      to={`${path}/${tabName}`}
      sx={{
        width: 1,
        height: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        backgroundColor: 'transparent',
        p: 1,
        px: 2.5,
        borderRadius: 0,
        color: 'text.primary',
        '&:hover': {
          color: 'primary.main',
        },
      }}
    >
      <Typography variant='paragraphRegular' sx={{ color: 'inherit', textTransform: 'capitalize' }}>
        {label}
      </Typography>
    </ButtonBase>
  );
});

export const MainNavItem = React.memo(function MainNavItem({
  nav,
  label,
  icon,
  iconRef,
  section,
}: {
  nav?: FeaturePage;
  label?: string;
  icon?: FeaturePage['icon'];
  iconRef?: React.RefObject<HTMLDivElement | null>;
  section?: string;
}) {
  const { classes, cx } = useNavItemStyles();
  const {
    anchorEl,
    popoverRef,
    handleEnterAnchor,
    handleMouseLeaveAnchor,
    handleClickItem,
    scheduleClose,
    cancelClose,
    isNavOpen,
  } = useNavItemPopover();
  const { collapsible } = useNavExpansion();
  const location = useLocation();
  const theme = useTheme();
  const isSelected = nav
    ? collapsible
      ? isNavSelected(nav.path, location.pathname, sectionedNavPaths)
      : nav.path === `/${location.pathname.split('/')[1]}`
    : false;
  const dispatch = useAppDispatch();
  const netWorthPageConfig = useAppSelector((state: RootState) =>
    reportConfigurationsSelectors.getReportConfigByUniqueIdentifierWeb(state, {
      getTabName: () => 'net-worth-page',
    }),
  );
  const showNav = useAppSelector((state) => (nav ? isNavItemVisible(state, nav) : false));

  if (!nav || !showNav) {
    return null;
  }
  const IconComponent = icon ?? nav.icon;

  const aId = makeNavigationItemId(nav);
  const displayTitle = label ?? nav.title;
  const { path } = nav;

  return (
    <>
      <Tooltip {...navTooltipProps} title={collapsible && !isNavOpen && !nav.popover ? displayTitle : null}>
        <ButtonBase
          onClick={() => {
            tracker.track(tracker.events.navMenuClick, { menuItem: nav.title, section });
            if (nav.title === 'Spending Plan') {
              tracker.track(tracker.events.viewSpendingPlan, { initiator: 'navigation bar' });
            }
            if (nav.path === '/upcoming/cash-flow') {
              tracker.track(tracker.events.viewCashFlow, { source: 'nav bar' });
            }
            if (nav.path === '/receipt-center') {
              trackReceiptEvent({ event: 'receiptCenterOpen', properties: { location: 'main-navigation' } });
            }
            if (nav.title === 'Net Worth' || nav.title === 'Dashboard') {
              if (nav.title === 'Net Worth') {
                tracker.track(tracker.events.viewNetWorthV2, { initiator: 'nav bar' });
              }

              if (netWorthPageConfig?.id) {
                dispatch(
                  generatedReportsActions.generateReport({
                    location: netWorthPageConfig.id,
                    configuration: netWorthPageConfig,
                  }),
                );
              }
            }
            handleClickItem();
          }}
          className={isSelected ? cx(classes.navMenuItem, classes.selectedItem) : classes.navMenuItem}
          to={nav.popover ? `${path}?tab=${nav.popover.default}` : path}
          component={collapsible ? Link : NavLink}
          aria-current={collapsible && isSelected ? 'page' : undefined}
          id={aId}
          aria-owns={nav.popover ? nav.popover.id : undefined}
          aria-haspopup={nav.popover ? 'true' : undefined}
          aria-describedby={nav.popover ? nav.popover.id : undefined}
          onMouseEnter={nav.popover ? handleEnterAnchor : undefined}
          onMouseLeave={nav.popover ? handleMouseLeaveAnchor : undefined}
        >
          {/* Below will keep the full nav item w/ darker background color when selected */}
          {/* IMPORTANT: this span is used in a css selector by the FutureTransactionGuidedPopover component */}
          <span ref={iconRef} className={classes.navBarIconSpan}>
            <IconComponent aria-label={nav.alt} className={classes.navIcon} />
          </span>
          <Stack
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            flexGrow={1}
            flexBasis={'100%'}
          >
            <Stack flexDirection={'row'} alignItems={'center'} flexGrow={collapsible ? 1 : undefined}>
              <Typography
                variant='paragraphRegular'
                variantMapping={NAV_MAPPING}
                className={isSelected ? classes.selectedNavText : classes.navText}
              >
                {displayTitle}
              </Typography>
              {nav.newChip ? (
                <Chip
                  key={`new-feature-${nav.title}`}
                  label={'New'}
                  size='small'
                  sx={{
                    backgroundColor: theme.palette.upcoming.light,
                    color: theme.palette.upcoming.dark,
                    ...(collapsible
                      ? { marginLeft: 'auto', marginRight: theme.spacing(1) }
                      : { marginLeft: theme.spacing(1) }),
                  }}
                />
              ) : null}
            </Stack>
            {nav.popover ? (
              collapsible ? (
                <KeyboardArrowRightRounded fontSize='small' className={classes.navFlyoutChevron} />
              ) : (
                <KeyboardArrowRight className={classes.navIcon} />
              )
            ) : null}
          </Stack>
        </ButtonBase>
      </Tooltip>
      {nav.popover ? (
        <Popper
          ref={popoverRef}
          onMouseLeave={scheduleClose}
          onMouseEnter={cancelClose}
          disablePortal={false}
          container={() => document.body}
          open={!!anchorEl}
          anchorEl={anchorEl}
          id={nav.popover.id}
          placement='right-start'
          transition={collapsible}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: collapsible ? [0, navItemInset + 6] : [-26, (isNavOpen ? 0 : navBarDiff) - 18],
              },
            },
            { name: 'preventOverflow', options: { padding: 8, rootBoundary: 'viewport' } },
          ]}
          sx={{
            pointerEvents: 'none',
            zIndex: 2000,
          }}
        >
          {({ TransitionProps }) => {
            const flyout = (
              <Paper elevation={16} sx={{ pointerEvents: 'auto', borderRadius: 2, m: collapsible ? 0 : 1.5 }}>
                <Stack
                  sx={{ ...(collapsible ? { width: 187 } : { minWidth: 155 }), alignItems: 'flex-start', pt: 1, pb: 1 }}
                >
                  {collapsible && !isNavOpen ? (
                    <Typography variant='captionMedium' color='text.secondary' sx={{ px: 2.5, py: 0.5 }}>
                      {displayTitle}
                    </Typography>
                  ) : null}
                  {nav.popover?.pages.map((page) => (
                    <PopperNavItem
                      key={page.tabName}
                      tabName={page.tabName}
                      label={page.label}
                      type={page.type}
                      path={path}
                      featureFlag={page.featureFlag}
                      handleClickItem={handleClickItem}
                      section='sales-and-billing'
                    />
                  ))}
                </Stack>
              </Paper>
            );
            return collapsible ? <Fade {...TransitionProps}>{flyout}</Fade> : flyout;
          }}
        </Popper>
      ) : null}
    </>
  );
});

export const RefreshNavItem = React.memo(function RefreshNavItem({ closeDrawer }: { closeDrawer: () => void }) {
  const { classes, cx } = useNavItemStyles();
  const { collapsible, expanded } = useNavExpansion();
  const button = (
    <RefreshButton
      closeHeader={closeDrawer}
      id='sync-arrow'
      classes={{ refreshDivButton: cx(classes.navMenuItem, collapsible && classes.refreshNavItem) }}
      sectioned={collapsible}
    />
  );

  if (!collapsible) {
    return button;
  }

  return (
    <Tooltip {...navTooltipProps} title={expanded ? null : 'Refresh'} describeChild>
      <div className={classes.refreshNavWrapper}>{button}</div>
    </Tooltip>
  );
});
